import { createFormDataRequest } from "@/helpers/FormHelpers";
import Meeting from "@/interfaces/Meeting";
import api from "@/services/api";

export interface MontageNote {
  id: string;
  message: string;
  createdAt: string;
  author?: { id: string; firstname: string; lastname: string } | null;
}

export interface RestrictedMontageMeeting {
  id: string;
  startAt: string;
  endAt?: string;
  durationMinutes?: number;
  calendarType: "montage";
  csmOwnerId: string | null;
  csmOwner: { id: string; firstname: string; lastname: string } | null;
  restricted: true;
}

export type MontageCalendarMeeting = Meeting | RestrictedMontageMeeting;

/** Créneau d'une heure déjà pris par un CSM. `companyName` est nul quand
 *  l'API masque l'entreprise à un commercial qui n'y est pas rattaché. */
export interface MontageBusySlot {
  start?: string;
  end?: string;
  hour: string;
  meetingId: string;
  companyName: string | null;
}

export interface CsmAvailability {
  slotMinutes?: number;
  csmId: string;
  firstname: string;
  lastname: string;
  busySlots: MontageBusySlot[];
  /** Plages du jour (spec 2026-10-08, lot 2). Absentes d'une ancienne réponse : rien n'est grisé. */
  ranges?: { start: string; end: string }[];
  unavailableReason?: "unset" | "off" | "holiday" | null;
  holidayName?: string | null;
}

export const montageMeetingsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMontageMeetingNotes: build.query<MontageNote[], string>({
      query: (meetingId) => ({ url: `/montage-meetings/${meetingId}/notes` }),
      providesTags: (_, __, meetingId) => [
        { type: "MontageMeetings" as const, id: `NOTES-${meetingId}` },
      ],
    }),
    storeMontageMeetingNote: build.mutation<
      MontageNote,
      { meetingId: string; message: string }
    >({
      query: ({ meetingId, message }) => ({
        url: `/montage-meetings/${meetingId}/notes`,
        method: "POST",
        body: createFormDataRequest({ message }),
      }),
      invalidatesTags: (_, __, { meetingId }) => [
        { type: "MontageMeetings", id: `NOTES-${meetingId}` },
      ],
    }),
    deleteMontageMeetingNote: build.mutation<
      void,
      { meetingId: string; id: string }
    >({
      query: ({ meetingId, id }) => ({
        url: `/montage-meetings/${meetingId}/notes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { meetingId }) => [
        { type: "MontageMeetings", id: `NOTES-${meetingId}` },
      ],
    }),
    // Filtres du panneau montage (statut, commercial, télépro, CSM), déjà
    // réduits à leurs paramètres d'API par toMontageCalendarParams.
    getMontageMeetings: build.query<
      MontageCalendarMeeting[],
      Record<string, unknown> | void
    >({
      query: (filters) => ({
        url: `/montage-meetings`,
        params: createFormDataRequest(filters ?? {}),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "MontageMeetings" as const,
                id,
              })),
              { type: "MontageMeetings" as const, id: "LIST" },
            ]
          : [{ type: "MontageMeetings" as const, id: "LIST" }],
    }),
    getMontageAvailability: build.query<CsmAvailability[], { date: string }>({
      query: (data) => ({
        url: `/montage-meetings/availability`,
        params: data,
      }),
      providesTags: [{ type: "MontageMeetings", id: "LIST" }],
    }),
    assignMontageMeetingStatus: build.mutation<
      Meeting,
      { meetingId: string; montageStatusId: string }
    >({
      query: ({ meetingId, montageStatusId }) => ({
        url: `/montage-meetings/${meetingId}/status`,
        method: "PATCH",
        body: { montageStatusId },
      }),
      invalidatesTags: (_, __, { meetingId }) => [
        { type: "MontageMeetings", id: meetingId },
        { type: "MontageMeetings", id: "LIST" },
      ],
    }),
    reassignMontageMeetingOwner: build.mutation<Meeting, [string, string]>({
      query: ([id, csmOwnerId]) => ({
        url: `/montage-meetings/${id}/owner`,
        method: "PATCH",
        body: { csmOwnerId },
      }),
      invalidatesTags: (_, __, [id]) => [
        { type: "CommercialBooking", id: "LIST" },
        { type: "MontageMeetings", id },
        { type: "MontageMeetings", id: "LIST" },
      ],
    }),
  }),

  overrideExisting: true,
});

export const {
  useGetMontageMeetingNotesQuery,
  useStoreMontageMeetingNoteMutation,
  useDeleteMontageMeetingNoteMutation,
  useGetMontageMeetingsQuery,
  useGetMontageAvailabilityQuery,
  useReassignMontageMeetingOwnerMutation,
  useAssignMontageMeetingStatusMutation,
} = montageMeetingsApi;
